import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { embedAPI, resolveImageUrl } from '../services/api';
import { stripHtmlToText } from '../utils/text';

export default function EmbedWidgetPage() {
  const { username } = useParams();
  const [searchParams] = useSearchParams();
  const theme = searchParams.get('theme') || 'dark';
  const limit = parseInt(searchParams.get('limit') || '4');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Use site origin so external embeds link back to the public domain (divispend.com)
  const backendUrl = (typeof window !== 'undefined' && window.location.origin) || process.env.REACT_APP_BACKEND_URL;
  const isDark = theme === 'dark';

  const loadProducts = useCallback(async () => {
    try {
      const res = await embedAPI.getProducts(username, theme, limit);
      setData(res.data);
    } catch (err) { console.error('Failed to load embed products:', err); }
    setLoading(false);
  }, [username, theme, limit]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  const bg = isDark ? '#111111' : '#FFFFFF';
  const cardBg = isDark ? '#1A1A1A' : '#F5F5F7';
  const border = isDark ? '#333333' : '#E5E5E5';
  const text = isDark ? '#FFFFFF' : '#111111';
  const textSec = isDark ? '#999999' : '#555555';

  if (loading) {
    return (
      <div style={{ backgroundColor: bg, minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui, sans-serif' }}>
        <p style={{ color: textSec }}>Loading...</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ backgroundColor: bg, minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui, sans-serif' }}>
        <p style={{ color: textSec }}>Store not found</p>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: bg, minHeight: '100%', padding: '16px', fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', paddingBottom: '12px', borderBottom: `1px solid ${border}` }}>
        {data.user.avatar_url ? (
          <img src={data.user.avatar_url} alt="" style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }} />
        ) : (
          <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#22C55E20', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#22C55E', fontWeight: 700, fontSize: '14px' }}>
            {data.user.name?.[0]?.toUpperCase() || 'U'}
          </div>
        )}
        <div>
          <p style={{ color: text, fontWeight: 700, fontSize: '14px', margin: 0 }}>{data.user.name}</p>
          <p style={{ color: '#22C55E', fontSize: '12px', fontWeight: 600, margin: 0 }}>${data.user.username}</p>
        </div>
        <a href={`${backendUrl}/store/${data.user.username}`} target="_blank" rel="noreferrer" style={{ marginLeft: 'auto', fontSize: '11px', color: '#22C55E', textDecoration: 'none', padding: '4px 10px', border: '1px solid #22C55E', borderRadius: '4px' }}>
          View Store
        </a>
      </div>

      {/* Products Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
        {data.products.map((product) => (
          <a
            key={product.product_id}
            href={`${backendUrl}/product/${product.product_id}`}
            target="_blank"
            rel="noreferrer"
            style={{
              display: 'block',
              backgroundColor: cardBg,
              border: `1px solid ${border}`,
              borderRadius: '4px',
              overflow: 'hidden',
              textDecoration: 'none',
              transition: 'transform 0.2s',
            }}
            data-testid={`embed-product-${product.product_id}`}
          >
            {product.image_url ? (
              <div style={{ aspectRatio: '16/10', overflow: 'hidden' }}>
                <img src={resolveImageUrl(product.image_url)} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            ) : (
              <div style={{ aspectRatio: '16/10', backgroundColor: isDark ? '#111' : '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={border} strokeWidth="1.5"><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M3 7l9-4 9 4"/></svg>
              </div>
            )}
            <div style={{ padding: '10px' }}>
              <p style={{ color: text, fontWeight: 700, fontSize: '13px', margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.name}</p>
              <p style={{ color: textSec, fontSize: '11px', margin: '0 0 6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{stripHtmlToText(product.description)}</p>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ color: '#22C55E', fontWeight: 800, fontSize: '15px' }}>{formatCurrency(product.price)}</span>
                <span style={{ fontSize: '10px', color: '#22C55E', padding: '2px 8px', border: '1px solid #22C55E', borderRadius: '3px' }}>Buy</span>
              </div>
            </div>
          </a>
        ))}
      </div>

      {/* Footer */}
      <div style={{ marginTop: '12px', paddingTop: '8px', borderTop: `1px solid ${border}`, textAlign: 'center' }}>
        <a href={backendUrl} target="_blank" rel="noreferrer" style={{ color: textSec, fontSize: '10px', textDecoration: 'none' }}>
          Powered by <span style={{ color: '#22C55E', fontWeight: 700 }}>Divispend</span>
        </a>
      </div>
    </div>
  );
}
